import { BankEl } from './style';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { FormFields } from '@src/components/FormFields';
import { GlobalEl } from '@src/styles/app';
import { useMutation } from 'react-query';
import DairyService, { IBank } from '@src/api/services/dairy';
import notify from '@src/components/Notify';
import { ESCAPE_KEY_CODE_VALUE } from '~/Constants';
import Drawer from '~/components/Drawer';
import { handleErrors } from '~/utils/helpers';

interface ICreateEditBanks {
  isVisible: boolean;
  id?: string;
  handleHiddenDrawer: () => void;
  isLoading?: boolean;
  fetchData?: () => void
}

const CreateEditBanks: React.FunctionComponent<ICreateEditBanks> = (props) => {
  const { isVisible, id, handleHiddenDrawer, isLoading } = props;
  const { t } = useTranslation();
  const { handleSubmit, control, reset, formState: { dirtyFields } } = useForm({
    mode: 'onSubmit'
  });
  const location = useLocation();
  const state = location.state as { status: string };
  const [bankEditData, setBankEditDataState] = useState<IBank>({
    id: '',
    name: ''
  });

  const { isLoading: isEditBankDataLoading, mutate: getBankById } = useMutation(
    'query-bank-by-id',
    DairyService.getDairyBankById,
    {
      onSuccess: (res) => {
        setBankEditDataState(res);
        reset(res)
      },
      onError: (error: any) => {
        handleErrors(error)
      }
    }
  );

  const { isLoading: isBankStoreLoading, mutate: createBank } = useMutation(
    'banks',
    DairyService.createDairyBank,
    {
      onSuccess: () => {
        notify(t('Successfully created bank'), 'success');
        props.fetchData()
      },
      onError: (error: any) => {
        handleErrors(error)
      }
    }
  );

  const { isLoading: isBankUpdateLoading, mutate: updateDairyBank } = useMutation(
    'banks',
    DairyService.updateDairyBank,
    {
      onSuccess: () => {
        notify(t('Successfully updated bank'), 'success');
        props.fetchData()
      },
      onError: (error: any) => {
        handleErrors(error)
      }
    }
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress)
    if (id && bankEditData) {
      getBankById(id);
      reset({ name: bankEditData.name });
    } else {
      reset({
        name: ''
      });
    }
    return () => document.removeEventListener('keydown', handleKeyPress)
  }, [id, isVisible]);

  function handleKeyPress(e) {
    if (e.keyCode === ESCAPE_KEY_CODE_VALUE) {
      reset()
      handleHiddenDrawer()
    }
  }

  const onSubmit = (data) => {
    try {
      if (!id) {
        createBank(data);
      } else {
        updateDairyBank({ id, bankName: data });
      }
      handleHiddenDrawer();
    } catch (error) {
      handleErrors(error)
    }
  };

  const renderContent = () => {
    return (
      <>
        <FormFields.Input
          rules={{ required: { value: true, message: t('Please enter name of the bank') } }}
          control={control}
          fluid
          label={t('Name of the bank')}
          name={'name'}
          placeholder={t('Name of the Bank')}
        />
        <BankEl.DrawerFooter>
          <GlobalEl.PrimaryButton
            disabled={Object.keys(dirtyFields).length === 0}
          >{id ? t('Update') : t('Add')}</GlobalEl.PrimaryButton>
        </BankEl.DrawerFooter>
      </>
    );
  };

  return <Drawer
    isVisible={isVisible || state?.status === 'open'}
    handleSubmit={handleSubmit(onSubmit)}
    isBusy={isLoading || isEditBankDataLoading}
    content={renderContent()}
    header={id ? t('Edit Bank') : t('Add New bank')}
    onHide={() => {
      reset();
      handleHiddenDrawer();
    }}
    closeOnEscape
  />
};

export default CreateEditBanks;
